import React, { useCallback, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useWatchHistory } from '@jwp/ott-hooks-react/src/useWatchHistory';
import { usePlaylistItemCallback } from '@jwp/ott-hooks-react/src/usePlaylistItemCallback';
import { useAds } from '@jwp/ott-hooks-react/src/useAds';
import useProtectedMedia from '@jwp/ott-hooks-react/src/useProtectedMedia';
import useBreakpoint, { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import type { JWPlayer } from '../../../types/jwplayer';
import Player from '../../components/Player/Player';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PlayerError, { PlayerErrorState } from '../../components/PlayerError/PlayerError';

type Props = {
  item: PlaylistItem;
  seriesItem?: PlaylistItem;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onNext?: () => void;
  feedId?: string;
  liveStartDateTime?: string | null;
  liveEndDateTime?: string | null;
  liveFromBeginning?: boolean;
  autostart?: boolean;
  nextItem?: PlaylistItem;
  ReConnecting : string
};

const PlayerContainer: React.FC<Props> = ({
  item,
  seriesItem,
  feedId,
  onPlay,
  onPause,
  onComplete,
  onUserActive,
  onUserInActive,
  onNext,
  liveEndDateTime,
  liveFromBeginning,
  liveStartDateTime,
  autostart,
  nextItem,
  ReConnecting
}: Props) => {
  // data
  const { data: adsData, isLoading: isAdsLoading } = useAds({ mediaId: item?.mediaid, item });
  const { data: playableItem, isLoading, isGeoBlocked } = useProtectedMedia(item);
  const { canShowSettings, isAutoPlay } = useAppConfig();
  const params = new URLSearchParams(window.location.search);
  const shouldInitializePlayer = params.get('play') === '1';

  const breakpoint = useBreakpoint();

  const isMobile = breakpoint === Breakpoint.xs;
  const isTablet = breakpoint === Breakpoint.sm || breakpoint === Breakpoint.md;
  const isLaptop = breakpoint === Breakpoint.lg || breakpoint === Breakpoint.xl;

  const getResponsiveImage = (item: PlaylistItem) => {
    try {
      if (!item?.images || item.images.length === 0) {
        return item.image ? item.image : '';
      }

      let targetWidth = 1920;

      if (isMobile) {
        targetWidth = 320;
      } else if (isTablet) {
        targetWidth = 720;
      } else if (isLaptop) {
        targetWidth = 1920;
      }

      const selectedImage = item.images.reduce((prev, curr) => {
        return Math.abs(curr.width - targetWidth) < Math.abs(prev.width - targetWidth) ? curr : prev;
      });

      return selectedImage?.src || item.image;
    } catch (error) {
      console.error('error', error);
    }
  };

  // state
  const [playerInstance, setPlayerInstance] = useState<JWPlayer>();

  // watch history
  const startTime = useWatchHistory(playerInstance, item, seriesItem);

  // player events
  const handleReady = useCallback((player?: JWPlayer) => {
    setPlayerInstance(player);
  }, []);

  const handleFirstFrame = useCallback(() => {
    // when playing a livestream, the first moment we can seek to the beginning of the DVR range is after the
    // firstFrame event.
    // @todo this doesn't seem to work 100% out of the times. Confirm with player team if this is the best approach.
    if (liveFromBeginning) {
      playerInstance?.seek(0);
    }
  }, [liveFromBeginning, playerInstance]);

  const handlePlaylistItemCallback = usePlaylistItemCallback(liveStartDateTime, liveEndDateTime);

  if (!playableItem || isLoading || isAdsLoading) {
    return <LoadingOverlay inline />;
  }

  if (isGeoBlocked) {
    return <PlayerError error={PlayerErrorState.GEO_BLOCKED} />;
  }

  const isAutoPlayNextVideo = canShowSettings && isAutoPlay && localStorage.getItem('autoplay') ? localStorage.getItem('autoplay') === 'true' : false;

  if (!shouldInitializePlayer) {
    return null;
  }

  return (
    <Player
      feedId={feedId}
      item={playableItem}
      adsData={adsData}
      onReady={handleReady}
      onFirstFrame={handleFirstFrame}
      onPlay={onPlay}
      onPause={onPause}
      onComplete={onComplete}
      onUserActive={onUserActive}
      onUserInActive={onUserInActive}
      onNext={onNext}
      onPlaylistItemCallback={handlePlaylistItemCallback}
      startTime={startTime}
      autostart={autostart}
      videoThumnail={getResponsiveImage(playableItem)}
      nextItem={nextItem}
      isAutoPlayNextVideo={isAutoPlayNextVideo}
      ReConnecting = {ReConnecting}
    />
  );
};

export default PlayerContainer;
